import React, { useState } from 'react';
import Avatar from './Avatar';
import { findOrCreateGroup } from '../services/groupMatching';
import { useLanguage } from '../i18n';

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .group-join-btn {
    transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s !important;
  }
  .group-join-btn:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 6px 20px rgba(0,128,129,0.4) !important;
  }
`;

export default function GroupMatchingPage({ user, userData, setUserData, theme, t: themeTokens, translate }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [created, setCreated] = useState(false);
  const [error, setError] = useState(null);

  const isDark = theme === 'dark';

  // THEME-AWARE COLORS - transparent backgrounds to match page
  const colors = {
    text: isDark ? '#ffffff' : '#1a1a2e',
    textSecondary: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
    textMuted: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)',
    textSubtle: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.4)',
    previewBg: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
    previewBorder: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
    featureBg: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
    featureBorder: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)',
    featureText: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
    successBg: isDark ? 'rgba(0,128,129,0.08)' : 'rgba(0,128,129,0.1)',
    errorBg: isDark ? 'rgba(248,113,113,0.08)' : 'rgba(248,113,113,0.1)',
    iconBg: isDark 
      ? 'linear-gradient(135deg, rgba(0,128,129,0.2), rgba(75,44,94,0.2))'
      : 'linear-gradient(135deg, rgba(0,128,129,0.15), rgba(75,44,94,0.15))',
  };

  const categories = [
    { key: 'fitnessHealth',    value: 'Fitness & Health' },
    { key: 'learningSkills',   value: 'Learning & Skills' },
    { key: 'businessCareer',   value: 'Business & Career' },
    { key: 'creativeProjects', value: 'Creative Projects' },
  ];

  const translateCategory = (categoryValue) => {
    const match = categories.find(c => c.value === categoryValue);
    return match ? t(match.key) : categoryValue;
  };

  const handleJoinGroup = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await findOrCreateGroup(user.uid, userData);
      if (!result) throw new Error('findOrCreateGroup returned null — check console for details');
      setUserData(prev => ({ ...prev, groupId: result.groupId }));
      setJoined(true);
      setCreated(result.created);
    } catch (err) {
      console.error('Group join error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (joined) {
    return (
      <div style={{ maxWidth: '32rem', margin: '0 auto', animation: 'fadeInUp 0.3s ease' }}>
        <style>{styleTag}</style>
        <div style={{ textAlign: 'center', padding: '2.5rem 2rem' }}>
          <div style={{
            width: '72px', 
            height: '72px', 
            margin: '0 auto 1.5rem',
            background: created
              ? (isDark 
                  ? 'linear-gradient(135deg, rgba(75,44,94,0.3), rgba(0,128,129,0.3))'
                  : 'linear-gradient(135deg, rgba(75,44,94,0.2), rgba(0,128,129,0.2))')
              : (isDark 
                  ? 'linear-gradient(135deg, rgba(0,128,129,0.3), rgba(75,44,94,0.3))'
                  : 'linear-gradient(135deg, rgba(0,128,129,0.2), rgba(75,44,94,0.2))'),
            border: `1px solid ${isDark ? 'rgba(0,128,129,0.3)' : 'rgba(0,128,129,0.25)'}`,
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center', 
            fontSize: '2rem'
          }}>
            {created ? '🌱' : '🎉'}
          </div>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '800', 
            marginBottom: '0.75rem', 
            color: colors.text, 
            letterSpacing: '-0.02em' 
          }}>
            {created ? t('groupCreated') : t('groupJoined')}
          </h2>
          <p style={{ 
            color: colors.textSecondary, 
            fontSize: '0.875rem', 
            lineHeight: 1.7, 
            marginBottom: '1.5rem' 
          }}>
            {created
              ? t('groupCreatedDesc').replace('{category}', translateCategory(userData.goalCategory))
              : t('groupJoinedDesc').replace('{category}', translateCategory(userData.goalCategory))
            }
          </p>
          <div style={{
            padding: '0.875rem 1rem',
            backgroundColor: colors.successBg,
            border: `1px solid ${isDark ? 'rgba(0,128,129,0.2)' : 'rgba(0,128,129,0.25)'}`,
            borderRadius: '0.875rem',
            fontSize: '0.8rem', 
            color: '#008081', 
            fontWeight: '500'
          }}>
            {t('switchToGroup')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '32rem', margin: '0 auto', animation: 'fadeInUp 0.3s ease' }}>
      <style>{styleTag}</style>
      
      {/* REMOVED: background, borderRadius, border, backdropFilter from here */}
      <div style={{ padding: '2rem' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '64px', 
            height: '64px', 
            margin: '0 auto 1rem',
            background: colors.iconBg,
            border: `1px solid ${isDark ? 'rgba(0,128,129,0.25)' : 'rgba(0,128,129,0.2)'}`,
            borderRadius: '50%', 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center', 
            fontSize: '1.75rem'
          }}>👥</div>
          <h2 style={{ 
            fontSize: '1.4rem', 
            fontWeight: '800', 
            marginBottom: '0.5rem', 
            color: colors.text, 
            letterSpacing: '-0.02em' 
          }}>
            {t('joinGroup')}
          </h2>
          <p style={{ 
            color: colors.textSecondary, 
            fontSize: '0.875rem', 
            lineHeight: 1.7 
          }}>
            {t('joinGroupDesc')}
          </p>
        </div>

        {/* User preview - kept subtle background since it's an inline element */}
        <div style={{
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.875rem',
          padding: '1rem 1.25rem',
          backgroundColor: colors.previewBg,
          border: `1px solid ${colors.previewBorder}`,
          borderRadius: '1rem', 
          marginBottom: '1.75rem'
        }}>
          <Avatar username={userData?.name} avatarId={userData?.avatarId} size={44} />
          <div>
            <div style={{ 
              fontWeight: '700', 
              color: colors.text, 
              fontSize: '0.95rem' 
            }}>
              {userData?.name}
            </div>
            <div style={{
              fontSize: '0.78rem', 
              marginTop: '0.15rem',
              color: '#008081', 
              fontWeight: '600',
              backgroundColor: isDark ? 'rgba(0,128,129,0.1)' : 'rgba(0,128,129,0.12)',
              display: 'inline-block', 
              padding: '0.1rem 0.5rem',
              borderRadius: '9999px', 
              border: `1px solid ${isDark ? 'rgba(0,128,129,0.2)' : 'rgba(0,128,129,0.25)'}`
            }}>
              {translateCategory(userData?.goalCategory)}
            </div>
          </div>
        </div>

        {/* What to expect */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{
            fontSize: '0.72rem', 
            fontWeight: '700',
            color: colors.textSubtle, 
            letterSpacing: '0.09em',
            textTransform: 'uppercase', 
            marginBottom: '0.875rem'
          }}>
            {t('whatToExpect')}
          </div>
          <div style={{ display: 'grid', gap: '0.6rem' }}>
            {[
              { icon: '🎭', text: t('expectAnonymous') },
              { icon: '🎯', text: t('expectMatched') },
              { icon: '👥', text: t('expectMembers') },
              { icon: '💬', text: t('expectChat') },
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '0.75rem',
                padding: '0.6rem 0.875rem',
                backgroundColor: colors.featureBg,
                border: `1px solid ${colors.featureBorder}`,
                borderRadius: '0.75rem',
                fontSize: '0.85rem', 
                color: colors.featureText
              }}>
                <span style={{ flexShrink: 0, fontSize: '1rem' }}>{item.icon}</span>
                <span style={{ lineHeight: 1.5 }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div style={{
            marginBottom: '1.25rem', 
            padding: '0.875rem 1rem',
            backgroundColor: colors.errorBg,
            border: `1px solid ${isDark ? 'rgba(248,113,113,0.25)' : 'rgba(248,113,113,0.3)'}`,
            borderRadius: '0.875rem',
            fontSize: '0.82rem', 
            color: '#f87171', 
            lineHeight: 1.6
          }}>
            ⚠️ {error}
          </div>
        )}

        <button
          className="group-join-btn"
          onClick={handleJoinGroup}
          disabled={loading}
          style={{
            width: '100%', 
            padding: '0.925rem',
            background: loading
              ? 'rgba(0,128,129,0.3)'
              : 'linear-gradient(135deg, #008081, #005f5f)',
            color: '#FFFFFF', 
            border: 'none',
            borderRadius: '0.875rem', 
            fontWeight: '700',
            fontSize: '0.95rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            boxShadow: loading ? 'none' : '0 4px 15px rgba(0,128,129,0.35)'
          }}
        >
          {loading ? t('findingGroup') : t('findOrCreate')}
        </button>

        <p style={{ 
          textAlign: 'center', 
          fontSize: '0.75rem', 
          color: colors.textMuted, 
          marginTop: '0.875rem', 
          lineHeight: 1.6 
        }}>
          {t('oneGroupNote')}
        </p>
      </div>
    </div>
  );
}